import React, { useCallback, useMemo, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { Button, Input } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import debounce from "lodash/debounce";
import {
  searchClientsAndGuarantors,
  loadMoreSearchResults,
  clearSearchResults,
} from "store/search";
import "./search.scss";

const MIN_QUERY_LENGTH = 2;

const TYPE_META = {
  client: {
    label: "Client",
    accent: "#52449f",
    tint: "#efedfb",
    icon: "nc-icon nc-circle-09",
  },
  guarantor: {
    label: "Guarantor",
    accent: "#ff9500",
    tint: "#fff3e0",
    icon: "nc-icon nc-badge",
  },
};

// Mirrors the semantic colors behind getStatusClassName's badge-* classes,
// used here as a scannable left-edge accent per row.
const STATUS_ACCENT = {
  ACTIVE: "#4f45ba",
  DEFAULT: "#ff4a55",
  DISBURSED: "#9a9a9a",
  "AWAITING DISBURSMENT": "#333333",
};
const NO_LOAN_ACCENT = "#e3e3e3";

const getInitials = (fullname = "") =>
  fullname
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const SearchCta = ({ to, icon, accent, children }) => (
  <Link to={to} className="search-cta" style={{ "--cta-accent": accent }}>
    <i className={icon} />
    {children}
    <i className="nc-icon nc-stre-right search-cta__arrow" />
  </Link>
);

const RowActions = ({ record }) => {
  const activeLoan = record.active_loan;
  const isClient = record.type === "client";

  if (!isClient && !activeLoan) {
    return <span className="search-loan-empty">No active loan</span>;
  }

  return (
    <div className="search-cta-group">
      {isClient && (
        <SearchCta
          to={`/clients/${record.id}`}
          icon="nc-icon nc-single-02"
          accent={TYPE_META.client.accent}
        >
          View Profile
        </SearchCta>
      )}
      {activeLoan && (
        <SearchCta
          to={`/loans/${activeLoan.id}/`}
          icon="nc-icon nc-money-coins"
          accent={STATUS_ACCENT[activeLoan.status] || NO_LOAN_ACCENT}
        >
          View Loan
        </SearchCta>
      )}
      {isClient && !activeLoan && (
        <span className="search-loan-empty">No active loan</span>
      )}
    </div>
  );
};

const ResultRow = ({ record }) => {
  const meta = TYPE_META[record.type];
  const accent = record.active_loan
    ? STATUS_ACCENT[record.active_loan.status] || NO_LOAN_ACCENT
    : NO_LOAN_ACCENT;

  return (
    <div className="search-result-row" style={{ borderLeftColor: accent }}>
      <div className="search-result-row__top">
        <div
          className="search-result-row__avatar"
          style={{ backgroundColor: meta.tint, color: meta.accent }}
        >
          {getInitials(record.fullname)}
        </div>
        <div className="search-result-row__main">
          <div className="search-result-row__name-line">
            <span className="search-result-row__name">{record.fullname}</span>
            <span className="search-result-row__type" style={{ color: meta.accent }}>
              <i className={meta.icon} />
              {meta.label}
            </span>
          </div>
          <div className="search-result-row__meta">
            {record.nin && <span>NIN {record.nin}</span>}
            {record.phone_number1 && <span>{record.phone_number1}</span>}
          </div>
        </div>
      </div>
      <div className="search-result-row__actions">
        <RowActions record={record} />
      </div>
    </div>
  );
};

const SearchHeader = ({ query, onChange, loading, compact }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: compact ? "32px 20px 24px" : "60px 20px",
    }}
  >
    <div
      style={{
        width: compact ? 64 : 88,
        height: compact ? 64 : 88,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #6f5ed1 0%, #52449f 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 20,
        boxShadow: "0 8px 24px rgba(82, 68, 159, 0.25)",
      }}
    >
      <i
        className="nc-icon nc-zoom-split"
        style={{ fontSize: compact ? 26 : 34, color: "#fff" }}
      />
    </div>
    <h4 style={{ marginBottom: 8 }}>Find a client or guarantor</h4>
    <p className="text-muted" style={{ maxWidth: 380, marginBottom: 24 }}>
      Search across your clients and guarantors by name or National ID
      Number (NIN). Results are limited to your org scope.
    </p>
    <Input.Search
      placeholder="Search by name or NIN..."
      value={query}
      onChange={onChange}
      allowClear
      loading={loading}
      size="large"
      style={{ width: "100%", maxWidth: 480 }}
    />
  </div>
);

const EmptyResults = ({ query }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "50px 20px",
    }}
  >
    <i
      className="nc-icon nc-simple-remove"
      style={{ fontSize: 40, color: "#d0d0d0", marginBottom: 16 }}
    />
    <h5>No matches found</h5>
    <p className="text-muted">
      Nothing matches "{query}". Try a different name or NIN.
    </p>
  </div>
);

const SearchPage = () => {
  const dispatch = useDispatch();
  const {
    query: persistedQuery,
    results,
    hasMore,
    page,
    loading,
    loadingMore,
  } = useSelector((state) => state.search);

  // Seed from whatever's already in the store (e.g. after navigating to a
  // profile/loan and back) so the page picks up where it left off instead
  // of resetting to the empty hero state.
  const [query, setQuery] = useState(() => persistedQuery || "");

  const runSearch = useMemo(
    () =>
      debounce((value) => {
        if (value.trim().length < MIN_QUERY_LENGTH) {
          dispatch(clearSearchResults());
          return;
        }
        dispatch(searchClientsAndGuarantors(value.trim()));
      }, 400),
    [dispatch]
  );

  const handleChange = useCallback(
    (e) => {
      const value = e.target.value;
      setQuery(value);
      runSearch(value);
    },
    [runSearch]
  );

  const handleLoadMore = useCallback(() => {
    dispatch(loadMoreSearchResults(query.trim(), page + 1));
  }, [dispatch, query, page]);

  const isSearchActive = query.trim().length >= MIN_QUERY_LENGTH;
  const noResults = isSearchActive && !loading && results.length === 0;

  return (
    <Container fluid className="search-page">
      <Row>
        <Col md="12">
          <Card>
            <Card.Body>
              <SearchHeader
                query={query}
                onChange={handleChange}
                loading={loading}
                compact={isSearchActive}
              />

              {isSearchActive && noResults && <EmptyResults query={query} />}

              {isSearchActive && !noResults && (
                <>
                  {results.length > 0 && (
                    <p className="text-muted" style={{ marginBottom: 12 }}>
                      Showing {results.length} result
                      {results.length === 1 ? "" : "s"}
                      {hasMore ? " (more available)" : ""} for "{query}"
                    </p>
                  )}

                  <div className="search-result-list">
                    {results.map((record) => (
                      <ResultRow key={`${record.type}-${record.id}`} record={record} />
                    ))}
                  </div>

                  {hasMore && (
                    <div style={{ textAlign: "center", marginTop: 20 }}>
                      <Button
                        onClick={handleLoadMore}
                        loading={loadingMore}
                        shape="round"
                      >
                        Load more results
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export { SearchPage };
